import React from 'react'
import { FaqAccordionElement } from '@shared/components/sections/faq/model'
import { ExternalPaths, Paths } from '@shared/menu-items/paths'
import { loansFaq } from 'data/loans-faq'
import Link from 'next/link'

import { faqPartnersPage } from './partners-page'

interface Requirement {
	name: string | null
	description?: string
	points: React.ReactNode[]
}

export interface ProductsPageCard {
	title: string
	subtitle: string
	hint?: string
	documents: string[]
	extraRequirements?: Requirement
	extraRequirementsSection?: {
		title: string
		requirements: Requirement[]
	}
}

export interface ProductPageData {
	mainBlockTitle: React.ReactElement | string
	card: ProductsPageCard
	loanStepsTitle: string
	faq: FaqAccordionElement[]
	formExtraHint?: string | React.ReactNode
	minLoan?: number
}

export enum ProductPagesTypes {
	realEstate,
	apartment,
	house,
	room,
	nonResidential,
	shareOfRealEstate,
}

export const productsPagesDataMap: { [key in ProductPagesTypes]: ProductPageData } = {
	[ProductPagesTypes.realEstate]: {
		mainBlockTitle: 'Деньги под залог недвижимости в %city_pr%',
		card: {
			title: 'Условия займа под залог недвижимости',
			subtitle:
				'Есть несколько условий, чтобы получить деньги под залог недвижимости. Познакомьтесь с ними, оставьте заявку и получите решение в течение дня.',
			hint: 'Услуги по выдаче займов физическим лицам под залог жилой недвижимости оказывает КПК «Фонд Ипотечного Кредитования».',
			documents: [
				'Паспорт',
				'Снилс',
				'ИНН',
				'Правоустанавливающий документ на недвижимость',
				'Для компаний понадобится Устав и документы учредителей. Иногда — отчётность в случаях требуемых законодательством',
			],
		},
		loanStepsTitle: 'Получить деньги под залог недвижимости просто',
		minLoan: 5e5,
		faq: loansFaq,
	},
	[ProductPagesTypes.apartment]: {
		mainBlockTitle: 'Деньги под залог квартиры в %city_pr%',
		card: {
			title: 'Условия займа под залог квартиры',
			subtitle:
				'Есть несколько условий, чтобы получить деньги под залог квартиры. Познакомьтесь с ними, оставьте заявку и получите решение в течение дня.',
			hint: 'Услуги по выдаче займов физическим лицам под залог жилой недвижимости оказывает КПК «Фонд Ипотечного Кредитования».',
			documents: [
				'Паспорт',
				'Снилс',
				'ИНН',
				'Правоустанавливающий документ на недвижимость',
				'Для компаний понадобится Устав и документы учредителей. Иногда — отчётность в случаях требуемых законодательством',
			],
			extraRequirements: {
				name: 'Требования к квартире',
				points: [
					'Нет несовершеннолетних собственников',
					'Этажность дома — от трёх и выше',
					'Любой возраст дома',
					'Можем рассмотреть объекты культурного наследия',
				],
			},
		},
		loanStepsTitle: 'Получить деньги под залог квартиры просто',
		minLoan: 5e5,
		faq: loansFaq,
	},
	[ProductPagesTypes.house]: {
		mainBlockTitle: <>Деньги под&nbsp;залог дома</>,
		card: {
			title: 'Условия займа под залог дома',
			subtitle:
				'Есть несколько условий, чтобы получить деньги под залог дома. Познакомьтесь с ними, оставьте заявку и получите решение в течение дня.',
			hint: 'Услуги по выдаче займов физическим лицам под залог жилой недвижимости оказывает КПК «Фонд Ипотечного Кредитования».',
			documents: [
				'Паспорт',
				'Снилс',
				'ИНН',
				'Правоустанавливающий документ на недвижимость',
				'Для компаний понадобится Устав и документы учредителей. Иногда — отчётность в случаях требуемых законодательством',
			],
			extraRequirements: {
				name: 'Требования к дому',
				points: [
					'Нет несовершеннолетних собственников',
					'Этажность дома — от трёх и выше',
					'Любой возраст дома',
					'Можем рассмотреть объекты культурного наследия',
				],
			},
		},
		loanStepsTitle: 'Получить деньги под залог дома просто',
		faq: [
			{
				title: 'Насколько быстрый процесс рассмотрения заявки?',
				content:
					'Деньги можем выдать в день обращения. Это возможно, если мы оперативно получаем требуемые документы от клиента и нам не требуется уточнение каких-либо деталей. Бывает, что выходим на сделку в течение 3–4 дней.',
			},
			{
				title: 'Важна ли кредитная история?',
				content:
					'Получение отказа из-за незначительных просрочек маловероятно. Мы детально изучаем каждую заявку и даём возможность каждому клиенту получить заём.',
			},
			{
				title: 'Не могу получить кредит в банке. Есть ли смысл подавать заявку у вас?',
				content:
					'Есть. Наша система скоринга менее формальна. Мы формируем собственный рейтинг, поэтому вероятность одобрения заявки на заём под залог недвижимости выше.',
			},
			{
				title: 'Требуется регулярное финансирование. Можете выдать средства по запросу?',
				content:
					'Да, у нас есть услуга — заёмная линия. Мы заключаем договор, устанавливаем лимит и по заявлению выдаём деньги.',
			},
			{
				title: 'Как снимается обременение?',
				content:
					'Как только к нам на счёт поступает последний платёж, мы подаём заявление на снятие в Росреестр.',
			},
			{
				title: 'Как быстро деньги поступят на счёт?',
				content:
					'Первая часть — в день в подписания договора. Остальные денежные средства поступают на счёт в течение 1–2 рабочих дней после регистрации сделки в Росреестре.',
			},
			{
				title: 'Есть ли законный способ продать обременённую квартиру?',
				content:
					'Если человек решил продать заложенную квартиру, мы идём навстречу. Сообщаем реквизиты, а после поступления средств на счёт пишем заявление на снятие обременения — объект можно продавать.',
			},
			{
				title: 'Какие законы регулируют вашу деятельность?',
				content: (
					<div>
						Их много. Основные: <br />— Федеральный закон «О микрофинансовой деятельности и
						микрофинансовых организациях» от 02.07.2010 №151-ФЗ. <br />
						— Федеральный закон «О привлечении инвестиций с использованием инвестиционных платформ и
						о внесении изменений в отдельные законодательные акты Российской Федерации» от
						02.08.2019 №259-ФЗ. <br />
						— Федеральный закон «О кредитной кооперации» от 18.07.2009 №190-ФЗ. <br />— Федеральный
						закон «Об ипотеке» от 16.07.1998 №102-ФЗ.
					</div>
				),
			},
			{
				title: 'Где можно посмотреть ваши правоустанавливающие документы?',
				content: (
					<div>
						Они находятся{' '}
						<Link href={Paths.documents} className='main-link'>
							на&nbsp;странице «Документы».
						</Link>
					</div>
				),
			},
			{
				title: 'Можно ли получить срочный заём?',
				content:
					'Взять заём под недвижимость можно за 24 часа. Главное — оперативно загрузить документы в личном кабинете.',
			},
			{
				title: 'Нет объекта для залога. Что делать?',
				content:
					'Чтобы получить деньги под залог недвижимости, залогодателем может стать третье лицо.',
			},
		],
	},
	[ProductPagesTypes.room]: {
		mainBlockTitle: <>Деньги под&nbsp;залог комнаты</>,
		card: {
			title: 'Условия займа под залог комнаты',
			subtitle:
				'Есть несколько условий, чтобы получить деньги под залог комнаты. Познакомьтесь с ними, оставьте заявку и получите решение в течение дня.',
			hint: 'Услуги по выдаче займов физическим лицам под залог жилой недвижимости оказывает КПК «Фонд Ипотечного Кредитования».',
			documents: [
				'Паспорт',
				'Снилс',
				'ИНН',
				'Правоустанавливающий документ на недвижимость',
				'Для компаний понадобится Устав и документы учредителей. Иногда — отчётность в случаях требуемых законодательством',
			],
			extraRequirements: {
				name: 'Требования к комнате',
				points: [
					'Рыночная стоимость — от 800 тысяч рублей',
					'Расположена в многоэтажном жилом доме, но не в общежитии',
				],
			},
		},
		loanStepsTitle: 'Получить деньги под залог комнаты просто',
		faq: [
			{
				title: 'Есть комната в общежитии. Подойдёт такой залог для заявки?',
				content:
					'Нет, общежитие не подойдёт. Мы рассмотрим объект, который расположен в обычном многоэтажном жилом доме.',
			},
			{
				title:
					'Что делать, если комната не в моей собственности? Может ли залогодателем быть другой человек?',
				content: (
					<div>
						Да, залогодателем может стать третье лицо: друг, родственник или партнёр. Кроме того,
						объектом залога может быть несколько видов недвижимости. Вы можете взять:
						<br />— деньги под залог комнаты,
						<br />— квартиры,
						<br />— доли,
						<br />— дома,
						<br />— офиса,
						<br />— торговой площади и др.
					</div>
				),
			},
			{
				title: 'Можно ли оформить срочный заём под комнату?',
				content: (
					<div>
						В среднем, мы выдаем денежные средства клиенту через 3–4 дня после подачи заявки. По
						повторным обращениям — в течение нескольких часов. Если вы станете постоянным клиентом,
						вы сможете взять срочный заём.
						<br />
						<br />
						Ещё одна возможность взять деньги под залог комнаты — оформить заёмную линию. С её
						помощью вы сможете по заявлению получить деньги в течение дня.
					</div>
				),
			},
			{
				title: 'Как быстро деньги поступят на счёт?',
				content:
					'Первая часть — в день подписания договора. Остальные денежные средства поступают на счёт в течение 1–2 рабочих дней после регистрации сделки в Росреестре.',
			},
			{
				title: 'Комната в коммунальной квартире подойдёт?',
				content:
					'Да, мы сможем рассмотреть объект в коммунальной квартире. Главное — рыночная стоимость комнаты должны быть выше 800 тысяч рублей.',
			},
			{
				title:
					'Взял деньги в банке под залог недвижимости. Хочу рефинансироваться — у вас есть такая услуга?',
				content:
					'Мы можем помочь с рефинансированием, если вы взяли кредит под залог недвижимости в другой компании. Для этого нужно получить согласие кредитора на повтор залога — только в этом случае мы сможем выйти к вам с выгодным предложением.',
			},
			{
				title:
					'У меня есть просроченная задолженность по действующему кредиту в банке. Вы даете только с хорошей кредитной историей?',
				content:
					'Мы сможем рассмотреть заявку, потому что работаем с клиентами, у которых разная кредитная история. К тому же, наличие других кредитов — не причина отказа в рассмотрении заявки и выдачи денег.',
			},
		],
	},
	[ProductPagesTypes.nonResidential]: {
		mainBlockTitle: <>Деньги под&nbsp;залог нежилого помещения</>,
		card: {
			title: 'Условия займа под залог коммерческой и нежилой недвижимости',
			subtitle:
				'Есть несколько условий, чтобы получить деньги под залог помещения, офиса или другой нежилой недвижимости.  Познакомьтесь с ними, оставьте заявку и получите решение в течение дня.',
			documents: [
				'Паспорт',
				'Снилс',
				'ИНН',
				'Правоустанавливающий документ на недвижимость',
				'Для компаний понадобится Устав и документы учредителей. Иногда — отчётность в случаях требуемых законодательством',
			],
			extraRequirementsSection: {
				title: 'Требования к объекту залога',
				requirements: [
					{
						name: null,
						description:
							'Ключевое требование — объект должен иметь ценность на рынке недвижимости. Вы можете взять:',
						points: [
							'— деньги под залог помещения,',
							'— апартаментов,',
							'— офисов,',
							'— торговых площадей,',
							'— гостиниц, отелей и хостелов,',
							'— паркингов,',
							'— земли,',
							'— капитальных гаражей,',
						],
					},
				],
			},
		},
		loanStepsTitle: 'Получить деньги под залог нежилого помещения просто',
		faq: [
			{
				title:
					'Недвижимость находится в залоге у банка. Хочу рефинансироваться — у вас есть такая услуга?',
				content:
					'Да, мы можем помочь с рефинансированием. Для этого нужно получить согласие кредитора на повтор залога — только в этом случае мы сможем выйти к вам с выгодным предложением.',
			},
			{
				title: 'Нам нужны деньги на коммерческий проект. Может ли залогодателем стать компания?',
				content: 'Да, компания или юридическое лицо может быть залогодателем.',
			},
			{
				title:
					'Что делать, если помещение не в моей собственности? Может ли залогодателем быть другой человек?',
				content: 'Да, залогодателем может стать третье лицо: друг, родственник или партнёр.',
			},
			{
				title: 'Коммерческое помещение находится за городом. Это имеет значение?',
				content:
					'Чтобы выдать деньги под залог коммерческой недвижимости, мы рассматривает объекты, которые находятся не дальше 40 км от города.',
			},
			{
				title:
					'У меня есть просроченная задолженность по действующему кредиту в банке. Вы даете только с хорошей кредитной историей?',
				content:
					'Мы сможем рассмотреть заявку, потому что работаем с клиентами, у которых разная кредитная история. К тому же, наличие других кредитов — не причина отказа в рассмотрении заявки и выдачи денег.',
			},
			{
				title: 'Можно ли оформить срочный заём под залог коммерческой недвижимости?',
				content:
					'В среднем, мы выдаем денежные средства клиенту через 3–4 дня после подачи заявки. По повторным обращениям — в течение нескольких часов. Если вы станете постоянным клиентом, вы сможете взять срочный заём.',
			},
			{
				title: 'Как быстро деньги поступят на счёт?',
				content:
					'Первая часть — в день подписания договора. Остальные денежные средства поступают на счёт в течение 1–2 рабочих дней после регистрации сделки в Росреестре.',
			},
		],
	},
	[ProductPagesTypes.shareOfRealEstate]: {
		mainBlockTitle: <>Деньги под&nbsp;залог доли недвижимости</>,
		formExtraHint: (
			<>
				Необходимо согласие всех собственников. Чтобы мы&nbsp;смогли рассмотреть заявку под залог
				доли недвижимости, каждый из&nbsp;собственников квартиры или дома должен стать
				залогодателем.
			</>
		),
		card: {
			title: 'Условия займа под залог доли недвижимости',
			subtitle:
				'Получить деньги под залог доли возможно при залоге квартиры или дома целиком. Чтобы мы смогли рассмотреть заявку, каждый из собственников должен стать залогодателем.',
			hint: 'Услуги по выдаче займов физическим лицам под залог жилой недвижимости оказывает КПК «Фонд Ипотечного Кредитования».',
			documents: [
				'Паспорт',
				'Снилс',
				'ИНН',
				'Правоустанавливающий документ на недвижимость',
				'Для компаний понадобится Устав и документы учредителей. Иногда — отчётность в случаях требуемых законодательством',
			],
			extraRequirementsSection: {
				title: 'Требования к объекту залога',
				requirements: [
					{
						name: 'Доля в квартире',
						points: [
							'Нет несовершеннолетних собственников',
							'Этажность дома — от трёх и выше',
							'Любой возраст дома',
							'Можем рассмотреть объекты культурного наследия',
						],
					},
					{
						name: 'Доля в доме',
						points: [
							'Достроенная капитальная жилая постройка не из дерева',
							'Есть отопление и коммуникации',
							'Находится в городе, посёлке или СНТ',
							'Не более 40 км от города',
							'Площадь — более 100 кв. м',
							'Нет несовершеннолетних собственников',
						],
					},
				],
			},
		},
		loanStepsTitle: 'Получить деньги под залог доли просто',
		faq: [
			{
				title: 'Можно ли оформить срочный заём?',
				content: (
					<div>
						В среднем, мы выдаем денежные средства клиенту через 3–4 дня после подачи заявки. По
						повторным обращениям — в течение нескольких часов. Если вы станете постоянным клиентом,
						вы сможете взять срочный заём.
						<br />
						<br />
						Ещё одна возможность взять деньги под залог комнаты — оформить заёмную линию. С её
						помощью вы сможете по заявлению получить деньги в течение дня.
					</div>
				),
			},
			{
				title: 'Можно ли оформить заём под залог ½ доли квартиры? Рассмотрите заявку?',
				content:
					'Да, заявку рассмотрим. Главное, чтобы другие собственники дали согласие на обременение квартиры целиком и стали залогодателями.',
			},
			{
				title:
					'Взял деньги в банке под залог недвижимости. Хочу рефинансироваться — у вас есть такая услуга?',
				content:
					'Мы можем помочь с рефинансированием, если вы взяли кредит под залог недвижимости в другой компании. Для этого нужно получить согласие кредитора на повтор залога — только в этом случае мы сможем выйти к вам с выгодным предложением.',
			},
			{
				title:
					'У меня есть просроченная задолженность по действующему кредиту в банке. Вы даете только с хорошей кредитной историей?',
				content:
					'Мы сможем рассмотреть заявку, потому что работаем с клиентами, у которых разная кредитная история. К тому же, наличие других кредитов — не причина отказа в рассмотрении заявки и выдачи денег.',
			},
			{
				title:
					'Что делать, если доля не в моей собственности? Может ли залогодателем быть другой человек?',
				content: (
					<div>
						Да, залогодателем может стать третье лицо: друг, родственник или партнёр. Кроме того,
						объектом залога может быть несколько видов недвижимости. Вы можете взять:
						<br />— деньги под залог комнаты,
						<br />— квартиры,
						<br />— доли,
						<br />— дома,
						<br />— офиса,
						<br />— торговой площади и др.
						<br />
						Подробности читайте на странице&nbsp;
						<Link href={Paths.productRealEstate} className='main-link'>
							займов под залог недвижимости
						</Link>
					</div>
				),
			},
			{
				title: 'Как быстро деньги поступят на счёт?',
				content:
					'Первая часть — в день подписания договора. Остальные денежные средства поступают на счёт в течение 1–2 рабочих дней после регистрации сделки в Росреестре.',
			},
		],
	},
}
